/* eslint-disable react/jsx-props-no-spreading */
import useAxios from 'axios-hooks';
import {
  format,
  parse,
  endOfMonth,
  startOfMonth,
  startOfToday,
  endOfWeek,
  isSameWeek,
  startOfWeek,
  // eslint-disable-next-line import/no-duplicates
} from 'date-fns';
import { filter, find, flatMap, union } from 'lodash-es';
import React, { useMemo, useState } from 'react';
import { useField, useFormState } from 'react-final-form';
import {
  DetailsEvent,
  InfoEvent,
  InfoEventHeader,
  InfoIcon,
  ModalPage,
  NotFoundAlert,
  PlateEvent,
  QuickSearch,
  WarningBox,
} from 'components';
import { DateTimePicker } from 'formComponents';
import {
  FormValues,
  ServiceGroupsInfo,
  MasterInfo,
  GroupEventData,
  GroupEventInfo,
  SpecialistCases,
} from 'types';
import { Box, Flex, IconButton, useMediaQuery } from '@chakra-ui/react';
import {
  LOCAL_TIMEZONE,
  SERVER_DATE_FORMAT,
  SERVER_DATE_TIMEZONE,
  TIMEZONE_GROUP_INFO,
} from 'constants/dates';
// eslint-disable-next-line import/no-duplicates
import { ru } from 'date-fns/locale';
import { ContainerStyled } from './Contacts/styles';

const initDayValue = (initialDate?: string | null) => {
  if (initialDate) {
    const inputDate = new Date(initialDate);
    const [year, month, date] = [
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate(),
    ];
    return new Date(year, month, date, 0, 0, 0);
  }

  return null;
};

const parseDates = (dates: string[]) =>
  dates.map((date) => parse(date, SERVER_DATE_FORMAT, new Date()));

export const GroupEvent: React.FC<{
  name: string;
  serviceGroups: ServiceGroupsInfo;
  masters: MasterInfo[];
  divisionId: string | null;
  specialistCases: SpecialistCases;
  timezone?: string;
}> = ({
  name,
  divisionId,
  masters,
  serviceGroups,
  specialistCases,
  timezone,
}) => {
  const {
    values: { widgetId },
  } = useFormState<FormValues>();
  const { input } = useField<GroupEventInfo | null>(name);
  const setInputValue = (value: GroupEventInfo) => () => {
    input.onChange(value);
  };
  const [day, setDay] = useState<Date | null>(() =>
    initDayValue(input.value?.date),
  );
  const today = new Date();
  const [isMobileDevice] = useMediaQuery('(max-width: 735px)');
  const [isWeekView, setWeekView] = useState(isMobileDevice);
  const [selectedPeriod, setSelectedPeriod] = useState(
    initDayValue(input.value?.date) || today,
  );

  const periodStart = (() => {
    if (isWeekView) {
      return isSameWeek(selectedPeriod, today, {
        locale: ru,
        weekStartsOn: 1,
      })
        ? startOfToday()
        : startOfWeek(selectedPeriod, { locale: ru, weekStartsOn: 1 });
    }
    return selectedPeriod.getMonth() === today.getMonth() &&
      selectedPeriod.getFullYear() === today.getFullYear()
      ? startOfToday()
      : startOfMonth(selectedPeriod);
  })();
  const periodEnd = isWeekView
    ? endOfWeek(selectedPeriod, { locale: ru, weekStartsOn: 1 })
    : endOfMonth(selectedPeriod);

  const [{ data, loading, error }] = useAxios<GroupEventData>(
    {
      url: '/booking-widget/client/visit-group',
      method: 'POST',
      data: {
        widgetId,
        startDate: format(periodStart, SERVER_DATE_TIMEZONE),
        endDate: format(periodEnd, SERVER_DATE_TIMEZONE),
        timeZone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(divisionId ? { divisionId } : {}),
      },
    },

    {
      manual: !widgetId,
    },
  );

  const dates = useMemo(
    () =>
      parseDates(
        data?.reduce((arrayDates: string[], currentData) => {
          return union(arrayDates, [currentData.date]);
        }, []) || [],
      ),
    [data],
  );

  // Быстрый поиск по названию события, ФИО специалиста и должности
  const [search, setSearch] = useState('');
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearch(event.target.value);

  const events = useMemo(() => {
    const pickDate = day ? format(day, SERVER_DATE_FORMAT) : '';
    return filter(data, (event) => event.date === pickDate);
  }, [data, day]);

  const filteredEvents = useMemo(() => {
    if (!search) return events;
    const newFilter = search.toLowerCase();
    const searchMasterNameOrPosition = (
      master: MasterInfo | undefined,
      str: string,
    ) =>
      !!master &&
      (master.name?.toLowerCase().includes(str) ||
        master.position?.toLowerCase().includes(str));

    return events.filter(
      (event) =>
        event.visitName?.toLowerCase().includes(newFilter) ||
        searchMasterNameOrPosition(
          find(masters, ({ id }) => id === event.employeeId),
          newFilter,
        ),
    );
  }, [events, masters, search]);

  const flattenServices = useMemo(
    () => flatMap(serviceGroups),
    [serviceGroups],
  );

  // Детальная информация по событию
  const [itemId, setItemId] = useState<string | null>(null);
  const item = useMemo(() => {
    if (itemId) {
      const event = find(events, ({ visitId }) => visitId === itemId);
      if (event) {
        const master = find(masters, ({ id }) => id === event.employeeId);
        const service = flattenServices.find(({ id }) => id === event.favorId);
        return { event, master, service };
      }
    }
    return null;
  }, [itemId, events, flattenServices, masters]);
  const resetItemId = () => setItemId(null);
  const handleOpenDetailsEvent =
    (eventId: string) => (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      setItemId(eventId);
      e.stopPropagation();
    };

  return (
    <div>
      <ModalPage isOpen={!!item} title="О событии" onClose={resetItemId}>
        {item && (
          <DetailsEvent
            widgetId={widgetId}
            {...item}
            specialistCases={specialistCases}
          />
        )}
      </ModalPage>
      <ContainerStyled>
        <Box w="100%">
          <DateTimePicker
            day={day}
            setDay={setDay}
            datesLoading={loading}
            datesError={error}
            includeDays={dates}
            name={name}
            onMonthChange={setSelectedPeriod}
            isWeekView={isWeekView}
            setWeekView={setWeekView}
            dayOnly
          />
        </Box>
        <Box w="100%">
          {!!events?.length && (
            <QuickSearch
              placeholder="Найти события"
              value={search}
              onChange={handleSearchChange}
            />
          )}
          {timezone && timezone !== LOCAL_TIMEZONE && (
            <WarningBox mb={4} message={TIMEZONE_GROUP_INFO} />
          )}
          {filteredEvents.length ? (
            <Box
              w="100%"
              borderWidth="1px"
              borderRadius="lg"
              borderColor="grayBorder"
            >
              {filteredEvents.map((event) => {
                const master = masters.find(
                  ({ id }) => id === event.employeeId,
                );
                const service = flattenServices.find(
                  ({ id }) => id === event.favorId,
                );
                const isAvailable =
                  event.registrationFinished !== true &&
                  !!event.available &&
                  event.available > 0;

                return (
                  <PlateEvent
                    key={event.visitId}
                    onClick={isAvailable ? setInputValue(event) : undefined}
                    _hover={
                      isAvailable
                        ? {
                            bgColor: '#EBF0F3',
                            '--event-header-bg': '#D9E2E7',
                          }
                        : undefined
                    }
                    cursor={isAvailable ? 'pointer' : 'default'}
                  >
                    <Flex
                      align="center"
                      justify="end"
                      backgroundColor="var(--event-header-bg, #F2F2F2)"
                      p={2}
                      minW="72px"
                    >
                      <InfoEventHeader
                        service={service}
                        event={event}
                        isAvailable={isAvailable}
                      />
                    </Flex>
                    <Flex align="center" justify="space-between" w="100%" p={2}>
                      <InfoEvent
                        master={master}
                        isAvailable={isAvailable}
                        {...event}
                      />
                      <IconButton
                        rounded="full"
                        variant="ghost"
                        icon={<InfoIcon />}
                        aria-label="Назад в выбор услуг"
                        onClick={handleOpenDetailsEvent(event.visitId)}
                      />
                    </Flex>
                  </PlateEvent>
                );
              })}
            </Box>
          ) : (
            <NotFoundAlert text="Событие не найдено" />
          )}
        </Box>
      </ContainerStyled>
    </div>
  );
};
